import { useTranslation } from 'react-i18next';
import { MapPin, Activity, AlertTriangle } from 'lucide-react';
import { useDashboardData } from '../hooks/useDashboardData';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import SiteSection from '../components/dashboard/SiteSection';

export default function Dashboard() {
  const { t } = useTranslation();
  const { sites, totalAlarms, isLoading, error } = useDashboardData();

  const totalSites = sites.length;
  const totalLoggers = sites.reduce((count, site) => count + site.loggers.length, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('dashboard.title')}</h1>
        <Card>
          <p className="text-red-600">{t('common.error')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('dashboard.title')}</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.totalSites')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalSites}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <MapPin className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.totalLoggers')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalLoggers}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Activity className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">{t('dashboard.activeAlarms')}</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalAlarms}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Vue Hiérarchique des Sondes */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.probeMonitoring')}</h2>
      </div>

      {sites.length > 0 ? (
        <div className="space-y-6">
          {sites.map((siteData) => (
            <SiteSection key={siteData.site.id} siteData={siteData} />
          ))}
        </div>
      ) : (
        <Card>
          <p className="text-gray-500">{t('common.noData')}</p>
        </Card>
      )}
    </div>
  );
}
